import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Button, CircularProgress, Alert,
  Stack, Checkbox, Dialog, DialogTitle, DialogContent, List, ListItemButton,
  ListItemText, Fab, Divider, Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { tasksAPI } from '../../services/api';
import { STATUS_LABELS, STATUS_COLORS } from './taskConstants';

/**
 * Vista del jefe de equipo: tareas agrupadas por tecnico, con "Sin asignar"
 * siempre arriba. Permite seleccionar varias y asignarlas de una vez.
 */
export default function AssignTasks({ onOpenTask, onCreateTask }) {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState([]);
  const [picker, setPicker] = useState(null);   // { taskIds, title }
  const [technicians, setTechnicians] = useState({ team: [], all: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await tasksAPI.board({ group_by: 'technician' });
      setGroups(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'No se han podido cargar las tareas');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /**
   * Abre la lista de tecnicos para una tarea suelta o para la seleccion.
   * Con una sola tarea se pasa su orden de fabricacion, para que arriba salga
   * quien ya esta planificado en esa OF.
   */
  const openPicker = async (taskIds, orderId, title) => {
    try {
      const res = await tasksAPI.assignableTechnicians(orderId || undefined);
      setTechnicians(res.data);
      setPicker({ taskIds, title });
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido cargar el equipo');
    }
  };

  const assignTo = async (technicianId) => {
    setBusy(true);
    try {
      for (const taskId of picker.taskIds) {
        await tasksAPI.assign(taskId, { technician_id: technicianId });
      }
      setSelected([]);
      setPicker(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido asignar');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (taskId) => {
    setSelected((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]);
  };

  if (error && !groups) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!groups) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  // La lista de asignables: equipo de la OF primero, y todos los activos debajo
  const others = technicians.all.filter((t) => !t.in_team);

  return (
    <Box sx={{ p: 1.5, pb: 12 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {groups.length === 0 && (
        <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
          No hay tareas en tus ordenes.
        </Typography>
      )}

      {groups.map((group) => (
        <Box key={group.key} sx={{ mb: 2.5 }}>
          <Typography variant="overline" color={group.key === 'unassigned' ? 'error' : 'text.secondary'}
            sx={{ pl: 0.5, fontWeight: 600 }}>
            {group.label} ({group.tasks.length})
          </Typography>
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {group.tasks.map((task) => (
              <Card key={task.id} elevation={1}>
                <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1,
                                   py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Checkbox checked={selected.includes(task.id)} onChange={() => toggle(task.id)}
                    sx={{ mt: -0.5 }} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ cursor: 'pointer' }} onClick={() => onOpenTask(task.id)}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>{task.title}</Typography>
                        <Chip size="small" label={STATUS_LABELS[task.status] || task.status}
                          color={STATUS_COLORS[task.status] || 'default'} />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {task.code} · {task.manufacturing_order_code || 'Sin orden'}
                        {task.planned_start ? ` · ${task.planned_start}` : ''}
                      </Typography>
                    </Box>
                    {/* Asignar desde la propia tarjeta: sin esto la unica via era
                        marcar el checkbox, y no se ve que exista */}
                    <Button size="small" startIcon={<PersonAddIcon />} sx={{ mt: 0.5, ml: -0.5 }}
                      color={group.key === 'unassigned' ? 'primary' : 'inherit'}
                      variant={group.key === 'unassigned' ? 'outlined' : 'text'}
                      onClick={() => openPicker(
                        [task.id], task.manufacturing_order_id, task.title,
                      )}>
                      {group.key === 'unassigned' ? 'Asignar' : 'Reasignar'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      ))}

      {/* Barra de accion cuando hay seleccion */}
      {selected.length > 0 && (
        <Box sx={{
          position: 'fixed', bottom: 56, left: 0, right: 0, p: 1.5,
          backgroundColor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
        }}>
          <Button fullWidth variant="contained" size="large" startIcon={<PersonAddIcon />}
            onClick={() => openPicker(selected, null,
              `${selected.length} tarea${selected.length > 1 ? 's' : ''}`)}
            sx={{ height: 52, fontSize: 16 }}>
            Asignar {selected.length} tarea{selected.length > 1 ? 's' : ''}
          </Button>
        </Box>
      )}

      {selected.length === 0 && (
        <Fab color="primary" onClick={onCreateTask} aria-label="Crear tarea"
          sx={{ position: 'fixed', bottom: 72, right: 16 }}>
          <AddIcon />
        </Fab>
      )}

      <Dialog open={!!picker} onClose={() => setPicker(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 1 }}>
          Asignar a
          {picker?.title && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {picker.title}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List disablePadding>
            {technicians.team.length > 0 && (
              <Typography variant="overline" sx={{ px: 2, pt: 1, display: 'block' }}>
                En esta orden
              </Typography>
            )}
            {technicians.team.map((t) => (
              <ListItemButton key={t.id} disabled={busy} onClick={() => assignTo(t.id)}>
                <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: t.color || 'primary.main',
                              fontSize: 14 }}>
                  {t.name?.[0]}
                </Avatar>
                <ListItemText primary={t.name} secondary={t.role || t.discipline} />
              </ListItemButton>
            ))}
            {others.length > 0 && (
              <>
                <Divider />
                <Typography variant="overline" sx={{ px: 2, pt: 1, display: 'block' }}>
                  Todos los tecnicos
                </Typography>
              </>
            )}
            {others.map((t) => (
              <ListItemButton key={t.id} disabled={busy} onClick={() => assignTo(t.id)}>
                <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: t.color || 'grey.500',
                              fontSize: 14 }}>
                  {t.name?.[0]}
                </Avatar>
                <ListItemText primary={t.name} secondary={t.role || t.discipline} />
              </ListItemButton>
            ))}
            <Divider />
            <ListItemButton disabled={busy} onClick={() => assignTo(null)}>
              <ListItemText primary="Quitar asignacion" />
            </ListItemButton>
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
